<script>
  import { useForm } from '@inertiajs/svelte'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FormLayout from '../../components/form_layout.svelte'
  import FormSection from '../../components/form_section.svelte'
  import PlaceSearch from '../../components/place_search.svelte'
  import GoogleMap from '../../components/google_map.svelte'
  import TextField from '../../components/form/text_field.svelte'
  import TextareaField from '../../components/form/textarea_field.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import ToggleField from '../../components/form/toggle_field.svelte'
  import NumberField from '../../components/form/number_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import ShrineImageField from '../../components/shrine_attachment_field.svelte'
  import { Button } from '../../components/ui'
  import { liveValidate } from '../../lib/live_validation.svelte.js'

  // Validation is the server's: each change is checked live by a Precognition request to the
  // real create/update action, and again on Save. Raw values go up; the server ignores blank
  // coordinates and an untouched image/logo, and stores untouched blank text as nil.
  let { area, back_url: backUrl, options = {} } = $props()

  const isEdit = !!area.id
  const url = isEdit ? `/college-towns/${area.id}` : '/college-towns'

  const form = useForm({
    area: {
      name: area.name,
      time_zone: area.time_zone,
      onboarding_text: area.onboarding_text,
      address: area.address,
      social_enabled: area.social_enabled,
      contact_sync_enabled: area.contact_sync_enabled,
      google_id: area.google_id,
      latitude: area.latitude,
      longitude: area.longitude,
      image: null,
      remove_image: false,
      logo: null,
      remove_logo: false
    },
    return_to: backUrl
  }).withPrecognition(isEdit ? 'patch' : 'post', url)
  liveValidate(form, 'area')

  const errorFor = (field) => $form.errors[`area.${field}`]

  let hours = $state([])

  const onPlaceSelect = (place) => {
    $form.area.address = place.address ?? $form.area.address
    // Persisting the picked google_id lets the server refresh Google metadata
    // (name/website/hours) on save via update_google_place — old CMS parity
    if (place.placeId) $form.area.google_id = place.placeId
    $form.area.latitude = place.lat
    $form.area.longitude = place.lng
    hours = place.hours ?? []
  }

  const submit = (event) => {
    event.preventDefault()
    if (isEdit) $form.patch(url)
    else $form.post(url)
  }
</script>

<Layout>
  <PageHeader title={isEdit ? 'Edit College Town' : 'New College Town'} subtitle="Cities and campuses where Zupp operates" />

  <FormLayout onsubmit={submit}>
    <FormSection title="Details">
      <TextField bind:value={$form.area.name} label="Name" name="name" error={errorFor('name')} required />
      <SelectField bind:value={$form.area.time_zone} label="Time Zone" name="time_zone" options={options.time_zones} error={errorFor('time_zone')} required />
      <TextareaField bind:value={$form.area.onboarding_text} label="Onboarding Text" name="onboarding_text" error={errorFor('onboarding_text')} />
    </FormSection>

    <FormSection title="Location">
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Google Maps</legend>
        <PlaceSearch onselect={onPlaceSelect} />
        <div class="mt-3">
          <GoogleMap bind:lat={$form.area.latitude} bind:lng={$form.area.longitude} />
        </div>
      </fieldset>

      <div class="rounded-box border border-base-300 p-4 space-y-2">
        <h3 class="font-semibold text-center">Location Information</h3>
        <div class="grid grid-cols-2 gap-4 text-sm">
          <NumberField bind:value={$form.area.latitude} label="Latitude" name="latitude" step="0.0001" error={errorFor('latitude')} />
          <NumberField bind:value={$form.area.longitude} label="Longitude" name="longitude" step="0.0001" error={errorFor('longitude')} />
        </div>
        <p><span class="font-medium">Address:</span> {$form.area.address || 'No address yet. Search a place above.'}</p>
        {#if $form.area.google_id}<p><span class="font-medium">Google ID:</span> {$form.area.google_id}</p>{/if}
        {#if area.google_maps_name}<p><span class="font-medium">Google Name:</span> {area.google_maps_name}</p>{/if}
        {#if area.website_url}<p><span class="font-medium">Website:</span> <a class="link" href={area.website_url} target="_blank" rel="noreferrer">{area.website_url}</a></p>{/if}
        {#if hours.length > 0}
          <div>
            <span class="font-medium">Hours:</span>
            {#each hours as line (line)}<p class="text-xs">{line}</p>{/each}
          </div>
        {/if}
      </div>
    </FormSection>

    <FormSection title="Media">
      <div class="grid grid-cols-2 gap-6">
        <ShrineImageField bind:value={$form.area.image} bind:remove={$form.area.remove_image} previewUrl={area.image_url} label="Image" />
        <ShrineImageField bind:value={$form.area.logo} bind:remove={$form.area.remove_logo} previewUrl={area.logo_url} label="Logo" />
      </div>
    </FormSection>

    <FormSection title="Settings">
      <ToggleField
        bind:checked={$form.area.social_enabled}
        label="Social"
        name="social_enabled"
        help="Gates the Social, Moments, and Quests features for this school. Off by default."
      />
      <ToggleField
        bind:checked={$form.area.contact_sync_enabled}
        label="Contact Sync"
        name="contact_sync_enabled"
        help="Lets the app match a user's address book to Zupp users and show Add / Invite. Needs Social on. Off by default."
        error={errorFor('contact_sync_enabled')}
      />
    </FormSection>

    {#if errorFor('base')}<p class="text-sm text-error">{errorFor('base')}</p>{/if}

    {#snippet footer()}
      <Button href={backUrl} variant="ghost" size="sm">Cancel</Button>
      <SubmitButton processing={$form.processing} full={false} />
    {/snippet}
  </FormLayout>
</Layout>
